<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex shrink-0 items-center justify-between">
            <h1 class="text-2xl font-semibold text-gray-700">Reports</h1>
            <UButton
                v-if="openInMetabaseUrl"
                :to="openInMetabaseUrl"
                target="_blank"
                variant="soft"
                icon="i-ph-arrow-square-out"
            >
                Open in Metabase
            </UButton>
        </div>

        <div class="flex-1 flex gap-4 overflow-hidden p-px">
            <div class="w-64 shrink-0 flex flex-col bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                <div class="flex shrink-0 gap-1 p-2 border-b border-gray-100">
                    <button
                        v-for="t in tabs"
                        :key="t.key"
                        @click="activeTab = t.key"
                        class="flex-1 px-3 py-1.5 rounded-md text-sm transition-colors"
                        :class="activeTab === t.key
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'"
                    >
                        {{ t.label }}
                    </button>
                </div>
                <div class="flex-1 overflow-y-auto">
                    <div v-if="pending" class="p-4 space-y-3">
                        <USkeleton v-for="i in 5" :key="i" class="h-10 w-full" />
                    </div>
                    <div v-else-if="reportsError" class="p-4 text-center">
                        <p class="text-sm text-red-500">Failed to load reports. Try again shortly.</p>
                    </div>
                    <div v-else-if="items.length" class="p-2">
                        <button
                            v-for="item in items"
                            :key="`${item.type}-${item.id}`"
                            @click="selectItem(item)"
                            class="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-md text-sm transition-colors"
                            :class="isSelected(item)
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-50'"
                        >
                            <UIcon :name="item.type === 'dashboard' ? 'i-ph-squares-four' : 'i-ph-chart-bar'" class="shrink-0" />
                            <span class="truncate">{{ item.name }}</span>
                        </button>
                    </div>
                    <div v-else class="p-4 text-center">
                        <p class="text-sm text-gray-400">{{ emptyMessage }}</p>
                    </div>
                </div>
                <div
                    v-if="usage"
                    class="shrink-0 flex items-start gap-1.5 px-3 py-2 border-t border-gray-100 text-xs"
                    :class="usageClass"
                >
                    <UIcon name="i-ph-robot" class="shrink-0 mt-px" />
                    <span>Metabot · {{ formatUsage(usage) }}<template v-if="usageLevel(usage.remaining) === 'low'"> · running low</template></span>
                </div>
            </div>

            <div class="flex-1 flex flex-col bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                <div v-if="embedLoading" class="flex-1 flex items-center justify-center">
                    <UIcon name="i-ph-spinner" class="text-3xl text-gray-300 animate-spin" />
                </div>
                <iframe
                    v-else-if="embedUrl"
                    :src="embedUrl"
                    class="w-full flex-1 border-0"
                    allowfullscreen
                />
                <div v-else-if="embedError" class="flex-1 flex items-center justify-center">
                    <div class="text-center space-y-2">
                        <UIcon name="i-ph-warning-duotone" class="text-4xl text-red-400" />
                        <p class="text-gray-500">{{ embedError }}</p>
                    </div>
                </div>
                <div v-else class="flex-1 flex items-center justify-center">
                    <div class="text-center space-y-2">
                        <UIcon name="i-ph-chart-bar-duotone" class="text-4xl text-gray-300" />
                        <p class="text-gray-400">Select a report to view</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { type MetabotUsage, formatUsage, usageLevel } from '~/utils/metabot-usage'

definePageMeta({
    layout: 'default',
    middleware: 'auth',
    roles: [UserRole.admin, UserRole.medicalDirector],
})

useHead({ title: 'Reports' })

interface ReportItem {
    id: number
    type: 'dashboard' | 'question'
    name: string
    description: string | null
    updatedAt: string | null
}

interface ReportsResponse {
    mine: ReportItem[]
    team: ReportItem[]
    hasMetabaseAccount: boolean
    metabaseUrl: string
}

const tabs = [
    { key: 'mine', label: 'My reports' },
    { key: 'team', label: 'Team reports' },
] as const

const user = useSupabaseUser()

const activeTab = ref<'mine' | 'team'>('mine')
const selectedItem = ref<ReportItem>()
const embedUrl = ref<string>()
const embedLoading = ref(false)
const embedError = ref<string>()

const { data: reports, pending, error: reportsError } = useFetch<ReportsResponse>('/api/metabase/reports')

const items = computed(() => reports.value?.[activeTab.value] ?? [])

const auth = useAuthStore()
const { data: usage } = useLazyFetch<MetabotUsage>('/api/metabase/usage', {
    immediate: !!auth.hasRole(UserRole.admin),
    server: false,
})

const usageClass = computed(() => {
    const level = usage.value ? usageLevel(usage.value.remaining) : 'ok'
    if (level === 'low') return 'text-red-600 bg-red-50'
    if (level === 'warn') return 'text-amber-700 bg-amber-50'
    return 'text-gray-500'
})

const emptyMessage = computed(() => {
    if (activeTab.value === 'team') {
        return 'No team reports yet. Save a question or dashboard to the Team Reports collection in Metabase.'
    }
    if (reports.value && !reports.value.hasMetabaseAccount) {
        return `No Metabase account for ${user.value?.email}. Ask an admin to create one with the same email.`
    }
    return 'No personal reports yet.'
})

const openInMetabaseUrl = computed(() => {
    const base = reports.value?.metabaseUrl
    if (!base) return undefined
    const item = selectedItem.value
    return item ? `${base}/${item.type}/${item.id}` : base
})

function isSelected(item: ReportItem) {
    return selectedItem.value?.id === item.id && selectedItem.value?.type === item.type
}

watch(activeTab, () => {
    selectedItem.value = undefined
    embedLoading.value = false
    embedUrl.value = undefined
    embedError.value = undefined
})

async function selectItem(item: ReportItem) {
    selectedItem.value = item
    embedLoading.value = true
    embedError.value = undefined
    embedUrl.value = undefined

    try {
        const { url } = await $fetch<{ url: string }>('/api/metabase/embed', {
            params: { type: item.type, id: item.id },
        })
        if (isSelected(item)) embedUrl.value = url
    } catch (e: any) {
        if (isSelected(item)) embedError.value = e?.data?.message || 'Failed to load report'
    } finally {
        if (isSelected(item)) embedLoading.value = false
    }
}
</script>
